import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import BlockUi from 'react-block-ui';
import moment from 'moment';
import { Link, useParams, useHistory } from 'react-router-dom';
import Api from '../../api/global.service';
import { Panel, PanelBody } from '../../components/panel/panel.jsx';
import FormTagsInput from '../../nuup/componentes/Forms/FormTagsInput.jsx';
import TokensExternosTokenModal from './tokens-externos-token-modal.jsx';

const endpoint = 'admin/tokens_externos';
const listaURL = '/admin/tokens/list';

const REGEX_DOMINIO = /^((\*\.)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}|[a-z0-9]([a-z0-9-]*[a-z0-9])?|\d{1,3}(\.\d{1,3}){3})$/i;
const REGEX_URL = /^\/[a-z0-9\-_./{}]*\*?$/i;
const REGEX_IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const REGEX_IPV6 = /^[0-9a-f]{0,4}(:[0-9a-f]{0,4}){2,7}$/i;

const TIPO_FRONTEND = 'frontend';
const TIPO_BACKEND = 'backend';

const DURACIONES = [
  { value: '1', label: '1 mes' },
  { value: '3', label: '3 meses' },
  { value: '6', label: '6 meses' },
  { value: '12', label: '12 meses' },
  { value: '24', label: '24 meses' },
  { value: 'personalizada', label: 'Fecha personalizada' },
  { value: 'sin_expiracion', label: 'Sin expiración' },
];

const schema = yup.object().shape({
  nombre: yup
    .string()
    .trim()
    .required('El nombre es requerido')
    .max(150, 'Máximo 150 caracteres'),
  descripcion: yup.string().nullable(),
});

const TokensExternosCRUD = () => {
  const { uuid } = useParams();
  const history = useHistory();
  const esNuevo = uuid == null;

  const [blocking, setBlocking] = useState(false);
  const [duracion, setDuracion] = useState('12');
  const [fechaPersonalizada, setFechaPersonalizada] = useState('');
  const [expiraGuardada, setExpiraGuardada] = useState(null);
  const [duracionTocada, setDuracionTocada] = useState(false);
  const [dominios, setDominios] = useState([]);
  const [urls, setUrls] = useState([]);
  const [ips, setIps] = useState([]);
  const [tipo, setTipo] = useState(TIPO_FRONTEND);
  const [rutasDisponibles, setRutasDisponibles] = useState([]);
  const [errorDominios, setErrorDominios] = useState(null);
  const [errorIps, setErrorIps] = useState(null);
  const [errorUrls, setErrorUrls] = useState(null);
  const [tokenGenerado, setTokenGenerado] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: { nombre: '', descripcion: '' },
  });

  useEffect(() => {
    Api.getAll(`${endpoint}/rutas_disponibles`)
      .then(response => setRutasDisponibles(response.data.data || []))
      .catch(() => setRutasDisponibles([]));
  }, []);

  useEffect(() => {
    if (esNuevo) return;

    setBlocking(true);
    Api.get(endpoint, uuid)
      .then(response => {
        const item = response.data.data;

        reset({ nombre: item.nombre, descripcion: item.descripcion || '' });
        setDominios(item.dominios_permitidos || []);
        setUrls(item.urls_permitidas || []);
        setIps(item.ips_permitidas || []);
        setTipo(item.tipo_integracion === TIPO_BACKEND ? TIPO_BACKEND : TIPO_FRONTEND);

        if (item.expira_at) {
          setExpiraGuardada(moment(item.expira_at).format('YYYY-MM-DD HH:mm:ss'));
          setFechaPersonalizada(moment(item.expira_at).format('YYYY-MM-DD'));
          setDuracion(item.duracion_meses ? String(item.duracion_meses) : 'personalizada');
        } else {
          setDuracion('sin_expiracion');
        }
        setDuracionTocada(false);

        setBlocking(false);
      })
      .catch(() => {
        setBlocking(false);
        Swal.fire({ icon: 'error', title: '', text: 'No se pudo cargar el token.' });
      });
  }, [uuid]);

  const normalizarDominio = valor =>
    (valor || '')
      .trim()
      .toLowerCase()
      .replace(/^[a-z]+:\/\//, '')
      .split('/')[0]
      .split('?')[0]
      .split(':')[0]
      .replace(/^\.+|\.+$/g, '');

  const validarDominio = valor =>
    REGEX_DOMINIO.test(normalizarDominio(valor)) ? null : 'Usa midominio.com, *.midominio.com, localhost o una IP';

  const validarUrl = valor => {
    const limpio = valor.replace(/^https?:\/\/[^/]+/i, '').split('?')[0];
    return REGEX_URL.test(limpio) ? null : 'Debe ser una ruta como /api/externo/ping y puede terminar en *';
  };

  const normalizarIp = valor => {
    let limpio = (valor || '')
      .trim()
      .toLowerCase()
      .replace(/^[a-z]+:\/\//, '');

    if (limpio.startsWith('[')) {
      limpio = limpio.slice(1).split(']')[0];
    } else if ((limpio.match(/:/g) || []).length === 1) {
      limpio = limpio.split(':')[0];
    }

    return limpio.replace(/^[/\s]+|[/\s]+$/g, '');
  };

  const esIpORango = valor => {
    const partes = valor.split('/');
    if (partes.length > 2) return false;

    const [direccion, bits] = partes;
    const esV4 = REGEX_IPV4.test(direccion);
    const esV6 = REGEX_IPV6.test(direccion);

    if (!esV4 && !esV6) return false;
    if (bits === undefined) return true;
    if (!/^\d+$/.test(bits)) return false;

    return Number(bits) <= (esV4 ? 32 : 128);
  };

  const validarIp = valor => (esIpORango(normalizarIp(valor)) ? null : 'Usa 200.1.2.3, ::1 o un rango como 200.1.2.0/24');

  const calcularExpiracion = () => {
    if (duracion === 'sin_expiracion') return null;
    // En edición, mientras no se toque la duración se conserva la fecha guardada.
    if (!esNuevo && !duracionTocada) return expiraGuardada;
    if (duracion === 'personalizada') {
      return fechaPersonalizada
        ? moment(fechaPersonalizada)
            .endOf('day')
            .format('YYYY-MM-DD HH:mm:ss')
        : null;
    }
    return moment()
      .add(parseInt(duracion, 10), 'months')
      .format('YYYY-MM-DD HH:mm:ss');
  };

  const expiracionCalculada = calcularExpiracion();

  const onSubmit = async values => {
    const esBackend = tipo === TIPO_BACKEND;

    if (!esBackend && dominios.length === 0) {
      setErrorDominios({ message: 'Debes indicar al menos un dominio permitido' });
      return;
    }
    setErrorDominios(null);

    if (esBackend && ips.length === 0) {
      setErrorIps({ message: 'Una integración de servidor debe indicar al menos una IP permitida' });
      return;
    }
    setErrorIps(null);

    if (urls.length === 0) {
      setErrorUrls({ message: 'Debes indicar al menos una URL permitida' });
      return;
    }
    setErrorUrls(null);

    if (duracion === 'personalizada' && !fechaPersonalizada) {
      await Swal.fire({ icon: 'warning', title: '', text: 'Indica la fecha de expiración.' });
      return;
    }

    if (duracion === 'sin_expiracion') {
      const confirma = await Swal.fire({
        icon: 'warning',
        title: 'Token sin expiración',
        text: 'El token seguirá funcionando indefinidamente hasta que lo revoques o lo elimines. ¿Continuar?',
        showCancelButton: true,
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Cancelar',
      });
      if (!confirma.isConfirmed) return;
    }

    const payload = {
      nombre: values.nombre,
      descripcion: values.descripcion || null,
      expira_at: expiracionCalculada,
      duracion_meses: duracion === 'personalizada' || duracion === 'sin_expiracion' ? null : parseInt(duracion, 10),
      tipo_integracion: tipo,
      dominios_permitidos: esBackend ? [] : dominios,
      urls_permitidas: urls,
      ips_permitidas: esBackend ? ips : [],
    };

    try {
      setBlocking(true);
      const response = esNuevo ? await Api.create(endpoint, payload) : await Api.update(endpoint, uuid, payload);
      setBlocking(false);

      if (esNuevo) {
        setTokenGenerado(response.data.token);
        return;
      }

      await Swal.fire({ icon: 'success', title: '', text: 'Token actualizado.' });
      history.push(listaURL);
    } catch (e) {
      setBlocking(false);
      const data = e.response ? e.response.data : {};
      const detalle = data.errors
        ? Object.values(data.errors)
            .flat()
            .join('\n')
        : data.message;
      await Swal.fire({ icon: 'error', title: '', text: detalle || 'No se pudo guardar el token.' });
    }
  };

  return (
    <>
      <h1 className="page-header">Tokens externos / {esNuevo ? 'Nuevo' : 'Editar'}</h1>

      <div className="row">
        <div className="col-md-12">
          <Panel>
            <PanelBody>
              <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="nombre">Nombre</label>
                        <input
                          id="nombre"
                          type="text"
                          className="form-control"
                          placeholder="Portal midominio.com"
                          {...register('nombre')}
                        />
                        {errors.nombre && <small className="text-danger">{errors.nombre.message}</small>}
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="descripcion">Descripción</label>
                        <input
                          id="descripcion"
                          type="text"
                          className="form-control"
                          placeholder="Para qué se usa este token"
                          {...register('descripcion')}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="duracion">Duración</label>
                        <select
                          id="duracion"
                          className="form-control"
                          value={duracion}
                          onChange={event => {
                            setDuracion(event.target.value);
                            setDuracionTocada(true);
                          }}>
                          {DURACIONES.map(opcion => (
                            <option key={opcion.value} value={opcion.value}>
                              {opcion.label}
                            </option>
                          ))}
                        </select>
                        <small className="text-muted">
                          {duracion === 'sin_expiracion'
                            ? 'El token no expira; solo se corta revocándolo o eliminándolo.'
                            : `Expira el ${expiracionCalculada ? moment(expiracionCalculada).format('DD/MM/YYYY') : '—'}`}
                        </small>
                      </div>
                    </div>
                  </div>

                  {duracion === 'personalizada' && (
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="fecha_personalizada">Fecha de expiración</label>
                          <input
                            id="fecha_personalizada"
                            type="date"
                            className="form-control"
                            value={fechaPersonalizada}
                            onChange={event => {
                              setFechaPersonalizada(event.target.value);
                              setDuracionTocada(true);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="tipo_integracion">Tipo de integración</label>
                        <select
                          id="tipo_integracion"
                          className="form-control"
                          value={tipo}
                          onChange={event => {
                            setTipo(event.target.value);
                            setErrorDominios(null);
                            setErrorIps(null);
                          }}>
                          <option value={TIPO_FRONTEND}>Navegador: la página consulta el API directamente</option>
                          <option value={TIPO_BACKEND}>Servidor: las llamadas salen de un backend</option>
                        </select>
                        <small className="text-muted d-block">
                          <i className="fa fa-info-circle mr-1" />
                          {tipo === TIPO_BACKEND
                            ? 'Se valida por IP.'
                            : 'Se valida por dominio. El navegador pone la cabecera Origin y otro sitio no puede falsearla.'}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    {tipo === TIPO_FRONTEND && (
                      <div className="col-md-6">
                        <FormTagsInput
                          id="dominios_permitidos"
                          label="Dominios permitidos"
                          value={dominios}
                          onChange={valores => {
                            setDominios(valores);
                            setErrorDominios(null);
                          }}
                          placeholder="midominio.com y Enter"
                          validar={validarDominio}
                          normalizar={normalizarDominio}
                          error={errorDominios}
                          ayuda="Solo se aceptan peticiones cuyo Origin o Referer pertenezca a estos dominios. Acepta *.midominio.com, localhost y direcciones IP para pruebas."
                        />
                      </div>
                    )}

                    {tipo === TIPO_BACKEND && (
                      <div className="col-md-6">
                        <FormTagsInput
                          id="ips_permitidas"
                          label="IPs permitidas"
                          value={ips}
                          onChange={valores => {
                            setIps(valores);
                            setErrorIps(null);
                          }}
                          placeholder="200.1.2.3 y Enter"
                          validar={validarIp}
                          normalizar={normalizarIp}
                          error={errorIps}
                          ayuda="Direcciones desde las que puede salir la petición. Acepta IPv4, IPv6 y rangos como 200.1.2.0/24."
                        />
                        <small className="text-muted d-block">
                          <i className="fa fa-info-circle mr-1" />
                          Si no sabes cuál es, pide al integrador que consulte /api/externo/ping desde su servidor: la respuesta trae la IP
                          que ve Sirio.
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <FormTagsInput
                        id="urls_permitidas"
                        label="URLs permitidas"
                        value={urls}
                        onChange={valores => {
                          setUrls(valores);
                          setErrorUrls(null);
                        }}
                        placeholder="/api/externo/ping y Enter"
                        sugerencias={rutasDisponibles}
                        validar={validarUrl}
                        error={errorUrls}
                        ayuda="Rutas de Sirio que este token puede consultar. Puedes usar * al final, por ejemplo /api/externo/*"
                      />
                      {urls.some(u => !u.endsWith('*') && !rutasDisponibles.includes(u)) && (
                        <small className="text-muted d-block">
                          <i className="fa fa-info-circle mr-1" />
                          Alguna URL todavía no existe en la API. Se guardará igual y funcionará cuando el endpoint se publique.
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="d-flex" style={{ gap: '8px' }}>
                    <button type="submit" className="btn btn-sirio" style={{ borderRadius: '20px', minWidth: 140 }}>
                      {esNuevo ? 'Generar token' : 'Guardar cambios'}
                    </button>
                    <Link
                      className="btn btn-outline-secondary btn-sirio"
                      style={{ borderRadius: '20px', color: '#374E00', borderColor: '#374E00' }}
                      to={listaURL}>
                      Cancelar
                    </Link>
                  </div>
                </form>
              </BlockUi>
            </PanelBody>
          </Panel>
        </div>
      </div>

      <TokensExternosTokenModal
        abierto={tokenGenerado !== null}
        token={tokenGenerado}
        onCerrar={() => {
          setTokenGenerado(null);
          history.push(listaURL);
        }}
      />
    </>
  );
};

export default TokensExternosCRUD;
